// GET_ORG_UNIT_LIST

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addOnboardRequried,
  getAllPositionData,
  getCityAndState,
  getClassification,
  getOnboardAddressListData,
  getOnboardEDMDetailsData,
  getOnboardingOutletTypesData,
  getOnboardingRequestData,
  getOnboardingRequestLogsData,
  getOnboardingStatusData,
  getOnBoardRequiredFieldsList,
  getOrganizationRecordsListsData,
  getOrgUnitListData,
  getSingleAddressData,
  getStateListData,
  getTerritoriesData,
  getVisitFrequencyData,
  updateBulkOnboardRequest,
  updateOnboardingAddress,
  updateOnboardingRequest,
  updateOnboardWindow,
} from "./OnBoardingRepo";
import { toast } from "react-toastify";
import { showToast } from "../../../utils/toastUtils";

export const useOrgunitListData = ({
  params = {},
  enable = false,
}: { params?: Record<string, any>; enable?: boolean } = {}) => {
  return useQuery({
    queryKey: ["ORGUNIT_LIST", params],
    queryFn: () => getOrgUnitListData(params),
    enabled: enable,
    retry: false,
  });
};

export const useOnboardRequestListData = ({
  params = {},
  enable = false,
}: { params?: Record<string, any>; enable?: boolean } = {}) => {
  return useQuery({
    queryKey: ["ONBOARD_REQUEST_LIST", params],
    queryFn: () => getOnboardingRequestData(params),
    enabled: enable,
    retry: false,
  });
};

// getOnboardingStatusData
export const useOnboardingStatusData = ({
  params = {},
  enable = false,
}: { params?: Record<string, any>; enable?: boolean } = {}) => {
  return useQuery({
    queryKey: ["ONBOARD_STATUS"],
    queryFn: getOnboardingStatusData,
    enabled: enable,
    retry: false,
  });
};

type StatusKey = string | number;
type FormattedOptions = { id: string; label: string }[];

export const useOnboardingStatusValue = ({
  statusKeys,
  enable = false,
}: {
  statusKeys: StatusKey | StatusKey[];
  enable?: boolean;
}) => {
  const { data, isLoading, error } = useOnboardingStatusData({ enable });

  const normalizeStatusKey = Array.isArray(statusKeys)
    ? statusKeys
    : [statusKeys];

  const result: Record<string, FormattedOptions> = {};

  if (data?.data) {
    normalizeStatusKey.forEach((key) => {
      const obj = data.data?.[key.toString()];
      if (obj && typeof obj === "object") {
        result[key.toString()] = Object.entries(obj).map(([id, label]) => {
          const isMaritalStatus = key.toString() === "MaritalStatus";
          const value = label as string;

          return {
            id: isMaritalStatus ? value : id,
            label: value,
          };
        });
      }
    });
  }

  return {
    values: result,
    isLoading,
    error,
  };
};

// getOnboardingOutletTypesData
export const useOnboardingOutletTypesData = ({
  params = {},
  enable = false,
}: { params?: Record<string, any>; enable?: boolean } = {}) => {
  return useQuery({
    queryKey: ["ONBOARD_OUTLET_TYPES"],
    queryFn: getOnboardingOutletTypesData,
    enabled: enable,
    retry: false,
  });
};

export const useOnboardingEDMDetailsData = ({
  id,
  enable = false,
}: {
  id?: string | number;
  enable?: boolean | undefined;
} = {}) => {
  return useQuery({
    queryKey: ["ONBOARD_EDM_DETAILS", id],
    queryFn: () => getOnboardEDMDetailsData(id!),
    enabled: enable && !!id,
    retry: false,
  });
};

export const useOnboardingAddressListData = ({
  id,
  enable = false,
  params = {},
}: {
  id?: string | number;
  enable?: boolean | undefined;
  params?: any;
} = {}) => {
  return useQuery({
    queryKey: ["ONBOARD_ADDRESS_LIST", id, params],
    queryFn: () => getOnboardAddressListData(id!, params!),
    enabled: enable && !!id,
    retry: false,
  });
};

// getOrganizationRecordsListsData
export const useOrganizationRecordsListsData = ({
  id,
  enable = false,
  params = {},
}: {
  id?: string | number;
  enable?: boolean | undefined;
  params?: any;
} = {}) => {
  return useQuery({
    queryKey: ["ORGANIATION_RECORDS", id, params],
    queryFn: () => getOrganizationRecordsListsData(id!, params!),
    enabled: enable && !!id,
    retry: false,
  });
};

// getOrganizationRecordsListsData
export const useOnboardingRequestLogsData = ({
  id,
  enable = false,
  params = {},
}: {
  id?: string | number;
  enable?: boolean | undefined;
  params?: any;
} = {}) => {
  return useQuery({
    queryKey: ["ONBOARDING_REQUEST_LOGS", id, params],
    queryFn: () => getOnboardingRequestLogsData(id!, params!),
    enabled: enable && !!id,
    retry: false,
  });
};

// getOnBoardRequiredFieldsList

export const useOnBoardRequiredFieldsListData = ({
  params = {},
  enable = false,
}: { params?: Record<string, any>; enable?: boolean } = {}) => {
  return useQuery({
    queryKey: ["ONBOARD_REQURIED", params],
    queryFn: () => getOnBoardRequiredFieldsList(params),
    enabled: enable,
    retry: false,
  });
};

export const useAddOnboardRequried = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addOnboardRequried,
    onSuccess: (data) => {
      console.log("data check in useHokk", data);
      showToast.success(`${data?.data?.message}`);
      queryClient.invalidateQueries({ queryKey: ["ONBOARD_REQURIED"] });
      // <Loader />
    },
    onError: (data: any) => {
      console.log(
        "🚀 ~ useOnBoard.tsx:217 ~ useAddOnboardRequried ~ data:",
        data?.response?.data?.message
      );

      showToast.warning(data?.response?.data?.message);
    },
  });
};

// getSingleAddressData
export const useSingleAddressData = ({
  id,
  enable = false,
}: {
  id?: string | number;
  enable?: boolean | undefined;
} = {}) => {
  return useQuery({
    queryKey: ["ONBOARD_SINGLE_ADDRESS", id],
    queryFn: () => getSingleAddressData(id!),
    enabled: enable && !!id,
    retry: false,
  });
};

export const useVisitFrequencyData = ({
  enable = false,
}: {
  enable?: boolean | undefined;
} = {}) => {
  return useQuery({
    queryKey: ["ONBOARD_VISIT_FREQUENCY"],
    queryFn: getVisitFrequencyData,
    enabled: enable,
    retry: false,
  });
};

export const useStateListData = ({
  enable = false,
}: {
  enable?: boolean | undefined;
} = {}) => {
  return useQuery({
    queryKey: ["ONBOARD_STATE_LIST"],
    queryFn: getStateListData,
    enabled: enable,
    retry: false,
  });
};

export const useGetTerritoriesData = ({
  enable = false,
  params,
}: {
  enable?: boolean | undefined;
  params?: any;
} = {}) => {
  return useQuery({
    queryKey: ["ONBOARD_TERRITORIES", params],
    queryFn: () => getTerritoriesData(params),
    enabled: enable,
    retry: false,
  });
};

export const useGetAllPositionData = ({
  enable = false,
  params,
}: {
  enable?: boolean | undefined;
  params?: any;
} = {}) => {
  return useQuery({
    queryKey: ["ONBOARD_POSITONS", params],
    queryFn: () => getAllPositionData(params),
    enabled: enable,
    retry: false,
  });
};

export const useCityAndStateData = ({
  enable = false,
  params,
}: {
  enable?: boolean | undefined;
  params?: any;
} = {}) => {
  return useQuery({
    queryKey: ["ONBOARD_CITY_STATE", params],
    queryFn: () => getCityAndState(params),
    enabled: enable,
    retry: false,
  });
};

export const useClassificationData = ({
  enable = false,
  params,
}: {
  enable?: boolean | undefined;
  params?: any;
} = {}) => {
  return useQuery({
    queryKey: ["ONBOARD_CLASSIFICATION", params],
    queryFn: () => getClassification(params),
    enabled: enable,
    retry: false,
  });
};

// updateOnboardingRequest

export const useUpdateOnboardingRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string | number; body: any }) =>
      updateOnboardingRequest(id, body),
    onSuccess: (data) => {
      showToast.success("Update Onboard Request!");
      queryClient.invalidateQueries({ queryKey: ["ONBOARD_EDM_DETAILS"] });
    },
    onError: (data: any) => {
      console.log(
        "🚀 ~ useOnBoard.tsx:342 ~ useUpdateOnboardingRequest ~ data:",
        data
      );

      showToast.warning("Failed to Onboard Request!");
    },
  });
};

export const useUpdateOnboardAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string | number; body: any }) =>
      updateOnboardingAddress(id, body),
    onSuccess: (data) => {
      showToast.success("Update Onboard Address!");
      queryClient.invalidateQueries({ queryKey: ["ONBOARD_ADDRESS_LIST"] });
      queryClient.invalidateQueries({ queryKey: ["ONBOARD_SINGLE_ADDRESS"] });
    },
    onError: (data: any) => {
      console.log(
        "🚀 ~ useOnBoard.tsx:364 ~ useUpdateOnboardAddress ~ data:",
        data?.response?.data?.message
      );

      // showToast.warning("Failed to Onboard Address!");
    },
  });
};

// updateBulkOnboardRequest
export const useUpdateBulkOnboardRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: any) => updateBulkOnboardRequest(body),
    onSuccess: (data) => {
      showToast.success("Update Onboard Request Status Successfully!");
      queryClient.invalidateQueries({ queryKey: ["ONBOARD_REQUEST_LIST"] });
      queryClient.invalidateQueries({ queryKey: ["ONBOARD_SINGLE_ADDRESS"] });
      queryClient.invalidateQueries({ queryKey: ["ONBOARD_EDM_DETAILS"] });
      queryClient.invalidateQueries({ queryKey: ["ONBOARDING_REQUEST_LOGS"] });
    },
    onError: (error) => {
      showToast.warning("Failed to Update Onboard Request!");
    },
  });
};

//GET_TERRITORY_LIST

export const useUpdateOnboardWindow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ body }: { body: any }) => updateOnboardWindow(body),
    onSuccess: (data) => {
      showToast.success("Update onboard window successfully!");
      queryClient.invalidateQueries({ queryKey: ["GET_TERRITORY_LIST"] });
    },
    onError: (data: any) => {
      console.log(
        "🚀 ~ useOnBoard.tsx:400 ~ useUpdateOnboardWindow ~ data:",
        data?.response?.data?.message
      );

      // showToast.warning(data?.response?.data?.message);
    },
  });
};

// GET_MEDIA_FILES_BY_IDS
